// src/pages/contentManagement/streaming/playlistConfig.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Select, Switch } from "antd";
import { FaListUl } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import { ADMIN_ROLES, MEDIA_ROLES, getStoredRoleNormalized } from "../../../auth/permissions";

// ─── Constants ─────────────────────────────────────────────────────────────────
const PLAYLIST_WRITE_ROLES = [...ADMIN_ROLES, ...MEDIA_ROLES];

const PLAYLIST_TYPE_OPTIONS = [
  { value: "sermon", label: "Sermons & Messages" },
  { value: "praiseWorship", label: "Praise & Worship" }
];

// ─── Component ─────────────────────────────────────────────────────────────────
function PlaylistConfig({ youtube, isCredentialsConfigured, onSaveSuccess, showNotification }) {
  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tracks dirty state per type independently.
  // { sermon: false, praiseWorship: false }
  const [dirtyMap, setDirtyMap] = useState({
    sermon: false,
    praiseWorship: false
  });

  const role = getStoredRoleNormalized();
  const canWrite = PLAYLIST_WRITE_ROLES.includes(role);

  const playlists = useMemo(() => youtube?.playlists || {}, [youtube]);

  // ── Load slot data into form when type selection changes ───────────────────
  useEffect(() => {
    if (!selectedType) {
      form.resetFields();
      return;
    }

    const existing = playlists[selectedType];
    form.setFieldsValue({
      playlistId: existing?.playlistId || "",
      isActive: existing?.isActive ?? false
    });
  }, [selectedType, playlists, form]);

  // ── Sync form when parent youtube prop updates after a save ────────────────
  useEffect(() => {
    if (!selectedType) return;

    const existing = playlists[selectedType];
    form.setFieldsValue({
      playlistId: existing?.playlistId || "",
      isActive: existing?.isActive ?? false
    });
  }, [youtube, selectedType, playlists, form]);

  // ── Handle type selection from dropdown ────────────────────────────────────
  const handleTypeChange = useCallback((value) => {
    setSelectedType(value);
    // Dirty state for the previous type is preserved in dirtyMap.
    // The dot indicator will remain on that option until it is saved.
  }, []);

  // ── Track dirty state when any field changes ───────────────────────────────
  const handleFieldsChange = useCallback(() => {
    if (!selectedType) return;
    setDirtyMap((prev) => ({ ...prev, [selectedType]: true }));
  }, [selectedType]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(
    async (values) => {
      if (!selectedType) return;

      setIsSubmitting(true);

      try {
        const token = await getAuthToken();

        const payload = {
          [selectedType]: {
            playlistId: values.playlistId.trim(),
            isActive: values.isActive ?? true
          }
        };

        const response = await axios.patch(API_ENDPOINTS.YOUTUBE_SETTINGS_PLAYLISTS, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Clear dirty indicator for this type only
        setDirtyMap((prev) => ({ ...prev, [selectedType]: false }));

        const slotLabel = PLAYLIST_TYPE_OPTIONS.find((o) => o.value === selectedType)?.label || selectedType;

        showNotification(`${slotLabel} playlist saved successfully.`, "success");
        onSaveSuccess(response.data);
      } catch (error) {
        console.error(`Error saving ${selectedType} playlist:`, error);
        showNotification(error.response?.data?.message || "Failed to save playlist. Please try again.", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedType, onSaveSuccess, showNotification]
  );

  // ── Render dropdown options with dirty dot ─────────────────────────────────
  const renderOption = useCallback(
    (option) => (
      <Select.Option key={option.value} value={option.value}>
        {option.label}
        {dirtyMap[option.value] && <span className="strm-playlist-dirty-dot" />}
      </Select.Option>
    ),
    [dirtyMap]
  );

  // ── Derived: is the form interactive ──────────────────────────────────────
  const isFormActive = canWrite && isCredentialsConfigured && Boolean(selectedType);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="strm-upload-section">
      <div className="strm-section-header">
        <FaListUl className="strm-section-icon" />
        <div>
          <h3 className="strm-section-title">Playlist Configuration</h3>
          <p className="strm-section-subtitle">
            {canWrite
              ? "Select a content type to configure its YouTube playlist."
              : "Playlist configuration is managed by administrators and media team leads."}
          </p>
        </div>
      </div>

      {/* ── Credentials gate ─────────────────────────────────────────────── */}
      {!isCredentialsConfigured && (
        <div className="strm-gate-notice">YouTube channel credentials must be configured before playlists can be activated.</div>
      )}

      {/* ── Type selector ─────────────────────────────────────────────────── */}
      <div className="strm-playlist-type-selector">
        <span className="strm-playlist-type-label">Content Type</span>
        <Select
          className="strm-playlist-type-select"
          placeholder="Select content type"
          value={selectedType}
          onChange={handleTypeChange}
          disabled={!canWrite || !isCredentialsConfigured}
        >
          {PLAYLIST_TYPE_OPTIONS.map(renderOption)}
        </Select>
      </div>

      {/* ── Playlist form ─────────────────────────────────────────────────── */}
      <Form form={form} layout="vertical" onFinish={handleSubmit} onFieldsChange={handleFieldsChange} className="strm-playlist-form">
        <Form.Item
          label="Playlist ID"
          name="playlistId"
          rules={[{ required: true, message: "Please enter the playlist ID" }]}
          extra="Found in the YouTube playlist URL after ?list="
        >
          <Input className="strm-upload-input" placeholder="PL..." disabled={!isFormActive} />
        </Form.Item>

        <Form.Item
          label="Active"
          name="isActive"
          valuePropName="checked"
          extra={
            !isCredentialsConfigured
              ? "YouTube credentials must be configured before activating a playlist."
              : !selectedType
                ? "Select a content type above to configure this playlist."
                : "Inactive playlists are hidden from the mobile app."
          }
        >
          <Switch disabled={!isFormActive} />
        </Form.Item>

        {canWrite && (
          <Form.Item>
            <Button className="strm-form-submit-btn" htmlType="submit" loading={isSubmitting} disabled={!isFormActive || isSubmitting}>
              {selectedType && playlists[selectedType]?.playlistId ? "Update Playlist" : "Save Playlist"}
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );
}

export default PlaylistConfig;
